import { Application, LibraryReference } from '@/types/application';
import { SubroutineFolder } from '@/types/subroutine';
import { getHighestNumber } from '@/util/number';
import { isNumber } from '@/util/typeGuards';

const LIBRARIES_LABEL = 'Libraries';
const PUBLIC_LABEL = 'Public';

// Adds `library` under its published `name`: appends the library reference and projects the
// library's Public folder into link subroutines under Libraries/<name>.
export const projectLibrary = (application: Application, library: Application, name: string): Application => {
  const publicFolder = library.folders?.find((folder) => folder.label === PUBLIC_LABEL && folder.parentId === 0);
  if (!publicFolder) {
    throw new Error(`${name} has no top-level Public folder, so it has nothing to link.`);
  }

  const folders = [...(application.folders ?? [])];
  let nextFolderId = getHighestNumber(folders, 'id');

  let librariesFolder = folders.find((folder) => folder.label === LIBRARIES_LABEL && folder.parentId === 0);
  if (!librariesFolder) {
    nextFolderId += 1;
    librariesFolder = { id: nextFolderId, parentId: 0, label: LIBRARIES_LABEL };
    folders.push(librariesFolder);
  }

  nextFolderId += 1;
  const libraryFolder: SubroutineFolder = { id: nextFolderId, parentId: librariesFolder.id, label: name };
  folders.push(libraryFolder);

  const folderIds: Record<number, number> = { [publicFolder.id]: libraryFolder.id };
  const folderPaths: Record<number, string> = { [publicFolder.id]: '' };

  const projectFolders = (parent: SubroutineFolder) => {
    library.folders
      ?.filter((folder) => folder.parentId === parent.id)
      .forEach((folder) => {
        nextFolderId += 1;
        const projected = { id: nextFolderId, parentId: folderIds[parent.id], label: folder.label };
        folderIds[folder.id] = projected.id;
        folderPaths[folder.id] = folderPaths[parent.id] ? `${folderPaths[parent.id]}.${folder.label}` : folder.label;
        folders.push(projected);
        projectFolders(folder);
      });
  };
  projectFolders(publicFolder);

  const subroutines = [...application.subroutines];
  let nextSubroutineId = getHighestNumber(subroutines, 'id');

  library.subroutines.forEach((subroutine) => {
    if (!isNumber(subroutine.folder) || !isNumber(folderIds[subroutine.folder])) return;

    const start = isNumber(subroutine.startblock) ? library.blocks.nodes[subroutine.startblock]?.type : undefined;
    if (!start) {
      throw new Error(`The Public subroutine ${subroutine.label} of ${name} has no start block.`);
    }

    const prefix = folderPaths[subroutine.folder];
    nextSubroutineId += 1;
    subroutines.push({
      id: nextSubroutineId,
      label: subroutine.label,
      folder: folderIds[subroutine.folder],
      nodes: [],
      args: subroutine.args.map((arg) => ({ ...arg })),
      arg_bindings: subroutine.args.map(() => ''),
      return_types: subroutine.return_types.map((returnType) => ({ ...returnType })),
      event_args: subroutine.event_args?.map((eventArg) => ({ ...eventArg })),
      event_arg_bindings: subroutine.event_args?.map(() => ''),
      link: { library: name, path: prefix ? `${prefix}.${subroutine.label}` : subroutine.label, start }
    });
  });

  const reference: LibraryReference = { name, revisionSelector: 'latest-published' };
  return {
    ...application,
    folders,
    subroutines,
    libraryReferences: [...(application.libraryReferences ?? []), reference]
  };
};

// The top-level folder that collects the added libraries.
export const findLibrariesFolder = (application: Application): SubroutineFolder | undefined =>
  application.folders?.find((folder) => folder.label === LIBRARIES_LABEL && folder.parentId === 0);

// The library folder (a direct subfolder of the top-level Libraries folder) holding the given folder.
export const findLibraryFolder = (
  application: Application,
  folderId: number | undefined
): SubroutineFolder | undefined => {
  const librariesFolder = findLibrariesFolder(application);
  if (!librariesFolder) return undefined;

  let current = application.folders?.find((folder) => folder.id === folderId);
  while (current && current.parentId !== librariesFolder.id) {
    current = application.folders?.find((folder) => folder.id === current?.parentId);
  }
  return current;
};

// Removes an added library: its folder subtree, every subroutine in it, and its libraryReferences record.
export const removeLibrary = (application: Application, libraryFolder: SubroutineFolder): Application => {
  const removedFolderIds = new Set([libraryFolder.id]);
  const collectFolders = (parentId: number) => {
    application.folders
      ?.filter((folder) => folder.parentId === parentId)
      .forEach((folder) => {
        removedFolderIds.add(folder.id);
        collectFolders(folder.id);
      });
  };
  collectFolders(libraryFolder.id);

  const folders = application.folders?.filter((folder) => !removedFolderIds.has(folder.id));
  const subroutines = application.subroutines.filter(
    (subroutine) => !isNumber(subroutine.folder) || !removedFolderIds.has(subroutine.folder)
  );

  // The Libraries folder goes too when the removed library was the last thing in it.
  const librariesId = libraryFolder.parentId;
  const librariesEmpty =
    !folders?.some((folder) => folder.parentId === librariesId) &&
    !subroutines.some((subroutine) => subroutine.folder === librariesId);

  return {
    ...application,
    folders: librariesEmpty ? folders?.filter((folder) => folder.id !== librariesId) : folders,
    subroutines,
    libraryReferences: application.libraryReferences?.filter((reference) => reference.name !== libraryFolder.label)
  };
};
